import { useEffect } from 'react';
import { Editor } from '@tiptap/core';
import { processRawMentions } from '../../utils/mentions';
import {
  isEditCommentAction,
  isReworkCommentAction,
} from '../../utils/commentActions';
import { Comment, CommentAction } from 'app/modules/comments/types';
import { UseCommentsProps } from 'app/components/postCommunication/Comments';
import {
  UnsavedValuesKeys,
  useUnsavedValues,
} from 'app/hooks/useUnsavedValues';
import { useMentionableUsers } from './useMentionableUsers';

export function useUnsavedComment({
  postId,
  type,
  editorRef,
  comment,
  commentAction,
  isLoading,
}: UseCommentsProps & {
  editorRef: Editor | undefined;
  comment?: Comment;
  commentAction: CommentAction | null;
  isLoading: boolean;
}) {
  const unsavedValue = useUnsavedValues();
  const mentionableUsers = useMentionableUsers({ postId, type });

  useEffect(() => {
    if (!editorRef) return;

    const key = UnsavedValuesKeys.commentInputOfPost({ postId, type });

    const unsavedComment = unsavedValue.get(key)?.value ?? '';

    editorRef.commands.setContent(
      processRawMentions(unsavedComment, mentionableUsers.resolvable),
      false,
      {
        preserveWhitespace: true,
      },
    );

    return () => {
      if (!editorRef) return;

      const isReworkBeingSaved =
        isLoading && isReworkCommentAction(commentAction);

      const value = editorRef.getText();

      if (value && !isEditCommentAction(commentAction) && !isReworkBeingSaved) {
        unsavedValue.add({ key, value });
      } else {
        unsavedValue.remove(key);
      }
    };
  }, [
    postId,
    type,
    editorRef,
    unsavedValue,
    mentionableUsers,
    commentAction,
    isLoading,
  ]);

  useEffect(() => {
    if (comment && isEditCommentAction(commentAction)) {
      editorRef?.commands.setContent(
        processRawMentions(comment.content, mentionableUsers.resolvable),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comment, commentAction]);
}
